import { useCallback, useEffect, useMemo, useState } from "react";
import { getOrderAuthorization, OrderAuthorizationEnum } from "@utils/getOrderAuthorization";
import { ICart, ICatalogOffer } from "@features/cart/ICart";
import {
    clearBroadbandPromoDrawerDismissed,
    getIsBroadbandPromoDrawerDismissed,
    getProspectReference,
    saveBroadbandPromoDrawerDismissed,
} from "@utils/storage";
import { useOffers } from "@hooks/useOffers";
import { useAddOffer } from "@hooks/useAddOffer";
import {
    checkCartOfferByServiceGroup,
    getIsAddingBroadbandOfferAllowed,
    selectOfferByServiceGroup,
    selectOfferInCartByServiceGroup,
} from "@utils/offerUtils";
import { EServiceGroup, ROUTES } from "@constants";
import { useHistory } from "react-router-dom";
import { useScroller } from "@hooks/useScroller";
import { IEirSalesPrequal } from "@modules/eirSalesFacade/interfaces/models/IEirSalesPrequal";
import { useDeviceType } from "@hooks/useDeviceType";
import { useDeleteOffer } from "@features/cart/hook/useDeleteOffer";

export const useHomeMobilePage = () => {
    const {
        offerList,
        offerListState,
        getOfferList,
        createProspectState,
        callCreateProspect,
        cart,
        setCart,
        cartState,
        getCart,
    } = useOffers();
    const {
        addMobileOfferState,
        addMobileOffer,
        addInclusiveEquipmentState,
        addBroadbandOffer,
        addBroadbandOfferState,
    } = useAddOffer();
    const { scrollToTop } = useScroller();
    const { isTabletOrDesktop, isMobile } = useDeviceType();

    const [orderAuthorization, setOrderAuthorization] = useState<OrderAuthorizationEnum | undefined>();
    const [isBroadbandSidePanelOpen, setIsBroadbandSidePanelOpen] = useState(false);

    const { push } = useHistory();

    const broadbandOffer = useMemo(() => selectOfferByServiceGroup(offerList, EServiceGroup.SA_BB), [offerList]);
    const mobileOffer = useMemo(() => selectOfferByServiceGroup(offerList, EServiceGroup.MOBILE), [offerList]);
    const broadbandCartOffer = useMemo(() => selectOfferInCartByServiceGroup(cart, EServiceGroup.SA_BB), [cart]);
    const hasMobileOfferInCart = useMemo(() => Boolean(selectOfferInCartByServiceGroup(cart, EServiceGroup.MOBILE)), [
        cart,
    ]);
    const mobileOfferCount = useMemo(
        () => cart?.offers?.filter((offer) => checkCartOfferByServiceGroup(offer, EServiceGroup.MOBILE)).length ?? 0,
        [cart]
    );
    const hasBroadbandOfferInCart = useMemo(() => Boolean(selectOfferInCartByServiceGroup(cart, EServiceGroup.SA_BB)), [
        cart,
    ]);

    useEffect(() => {
        if (mobileOfferCount === 0) {
            clearBroadbandPromoDrawerDismissed();
        }
    }, [mobileOfferCount]);

    useEffect(() => {
        setOrderAuthorization(getOrderAuthorization(cart));
    }, [cart]);

    const openBroadbandSidePanel = () => setIsBroadbandSidePanelOpen(true);
    const closeBroadbandSidePanel = () => {
        saveBroadbandPromoDrawerDismissed();
        setIsBroadbandSidePanelOpen(false);
    };

    const handleAddMobileOffer = useCallback(
        (selectedOffer: ICatalogOffer) => {
            const prospectReference = getProspectReference();

            if (prospectReference) {
                addMobileOffer(selectedOffer.catalogOfferCode, prospectReference).then((newCart?: ICart) => {
                    if (newCart) {
                        setCart(newCart);

                        const mobileOffersInCartLength = newCart.offers.filter((offer) =>
                            checkCartOfferByServiceGroup(offer, EServiceGroup.MOBILE)
                        ).length;

                        if (mobileOffersInCartLength >= 2) {
                            push(ROUTES.CART_PAGE_URL);

                            return;
                        }

                        const isAddingBroadbandOfferAllowed = getIsAddingBroadbandOfferAllowed(newCart);
                        if (
                            broadbandOffer &&
                            isAddingBroadbandOfferAllowed &&
                            !getIsBroadbandPromoDrawerDismissed() &&
                            mobileOffersInCartLength === 1
                        ) {
                            scrollToTop();
                            openBroadbandSidePanel();
                        } else {
                            push(ROUTES.CART_PAGE_URL);
                        }
                    }
                });
            }
        },
        [addMobileOffer, broadbandOffer, push, scrollToTop, setCart]
    );

    const handleAddBroadbandOffer = useCallback(
        (selectedOffer: ICatalogOffer, prequal: IEirSalesPrequal) => {
            const prospectReference = getProspectReference();

            if (prospectReference && prequal.prequalId) {
                addBroadbandOffer(selectedOffer.catalogOfferCode, prequal.prequalId, prospectReference).then(
                    (newCart?: ICart) => {
                        if (newCart) {
                            setCart(newCart);
                        }
                    }
                );
            }
        },
        [addBroadbandOffer, setCart]
    );

    const { handleDeleteOffer } = useDeleteOffer({ setCart });
    const handleDeleteBroadbandOffer = useCallback(async () => {
        const broadbandOfferInCart = selectOfferInCartByServiceGroup(cart, EServiceGroup.SA_BB);

        if (broadbandOfferInCart) {
            await handleDeleteOffer(broadbandOfferInCart);
        }
    }, [cart, handleDeleteOffer]);

    const isLoadingList = [
        offerListState.isLoading,
        cartState.isLoading,
        createProspectState.isLoading,
        addMobileOfferState.isLoading,
        addInclusiveEquipmentState.isLoading,
    ];
    const isLoading = isLoadingList.some((loading) => loading);
    const errorOptions = [
        offerListState.error,
        cartState.error,
        createProspectState.error,
        addMobileOfferState.error,
        addInclusiveEquipmentState.error,
    ];
    const error = errorOptions.find((isError) => isError);

    const getRetryFunction = useCallback(() => {
        if (offerListState.error) {
            return getOfferList;
        }
        if (cartState.error) {
            return getCart;
        }
        if (createProspectState.error) {
            return callCreateProspect;
        }

        return () => window.location.reload();
    }, [cartState.error, createProspectState.error, offerListState.error, callCreateProspect, getCart, getOfferList]);

    const handleBannerButtonClick = () => {
        if (hasMobileOfferInCart) {
            push(ROUTES.CART_PAGE_URL);
        } else if (mobileOffer) {
            handleAddMobileOffer(mobileOffer);
        }
    };

    return {
        broadbandOffer,
        mobileOffer,
        broadbandCartOffer,
        hasMobileOfferInCart,
        mobileOfferCount,
        hasBroadbandOfferInCart,
        isBroadbandSidePanelOpen,
        closeBroadbandSidePanel,
        isLoading,
        error,
        orderAuthorization,
        handleAddMobileOffer,
        handleAddBroadbandOffer,
        handleDeleteBroadbandOffer,
        addBroadbandOfferState,
        retry: getRetryFunction(),
        handleBannerButtonClick,
        isTabletOrDesktop,
        isMobile,
    };
};
